import { isTaskActive, TaskKind, TaskStatus, type TaskDto } from '@gr/core/platform/tasks/dto';

// TASK-UC-2, TASK-UC-3: как показывать задачу — без React.

/** Тон бейджа состояния (ui-kit Badge). */
export type TaskTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';

/** Ключ словаря вида задачи (в TaskKind есть точка, а в ключах словаря она — разделитель). */
export function taskKindKey(kind: TaskKind): string {
  switch (kind) {
    case TaskKind.MediaRecompress:
      return 'mediaRecompress';
    case TaskKind.MediaStorageRecount:
      return 'mediaStorageRecount';
    case TaskKind.Backup:
      return 'operationsBackup';
    case TaskKind.Restore:
      return 'operationsRestore';
  }
}

/** Раздел настроек блога со списком задач (TASK-UC-3): settings/:section. */
export const BLOG_TASKS_SECTION = 'tasks';

export function taskTone(task: TaskDto): TaskTone {
  switch (task.status) {
    case TaskStatus.Queued:
      return 'neutral';
    case TaskStatus.Running:
      return task.cancelRequested ? 'warning' : 'accent';
    case TaskStatus.Paused:
      return 'warning';
    case TaskStatus.Completed:
      return 'success';
    case TaskStatus.CompletedWithErrors:
      return 'danger';
    case TaskStatus.Cancelled:
      return 'neutral';
  }
}

/**
 * Готовность 0…1: по объёму, если вид его считает (видео весят больше фото), иначе по шагам; доля
 * текущего шага прибавляется.
 */
export function taskFraction(task: TaskDto): number | null {
  const { progress } = task;
  if (task.status === TaskStatus.Completed || task.status === TaskStatus.CompletedWithErrors) return 1;
  if (progress.total === 0) return task.status === TaskStatus.Queued ? 0 : null;
  const current = progress.current?.fraction ?? 0;
  if (progress.bytesTotal && progress.bytesDone !== null) {
    const stepShare = progress.total > progress.done ? (progress.bytesTotal - progress.bytesDone) / (progress.total - progress.done) : 0;
    return Math.min(1, (progress.bytesDone + current * stepShare) / progress.bytesTotal);
  }
  return Math.min(1, (progress.done + current) / progress.total);
}

/**
 * TASK-UC-2, TASK-UC-2.CC-2: задачи индикатора — блога и, администратору, платформы; идущие
 * первыми, затем недавно завершённые, новые раньше старых.
 */
export function indicatorTasks(tasks: Iterable<TaskDto>, blogId: string | null, isAdmin: boolean): TaskDto[] {
  return [...tasks]
    .filter((task) => (task.blogId !== null && task.blogId === blogId) || (isAdmin && task.blogId === null))
    .sort((a, b) => Number(isTaskActive(b.status)) - Number(isTaskActive(a.status)) || b.createdAt.localeCompare(a.createdAt));
}

export function activeCount(tasks: readonly TaskDto[]): number {
  return tasks.filter((task) => isTaskActive(task.status)).length;
}

/** Можно ли отменить задачу (TASK-UC-4); права проверяет вызывающий. */
export function cancellable(task: TaskDto): boolean {
  return isTaskActive(task.status) && !task.cancelRequested;
}

export function resumable(task: TaskDto): boolean {
  return task.status === TaskStatus.Paused;
}
